import React, { useState } from 'react';
import {
  CalculationWidget,
  CalculationWidgetProps
} from '@gjensidige/se-common-calculation';
import { useFormValidation } from '@gjensidige/se-form-validation';
import type { ValidationRule, FieldValidation, FieldType, CommonValidationParams } from '@gjensidige/se-form-validation';

import { AppHelper } from '../utils/AppHelper';

interface WidgetProps {
  enonicData: {
    headerText: string;
    imageAltText: string;
    regNumberLabel: string;
    regNumberPlaceHolder: string;
    cprNumberLabel: string;
    cprNumberPlaceHolder: string;
    cprNumberHelpText: string;
    calculateBtnLabel: string;
    footerMessage: string;
    imageLocations: {
      image: string;
      smallScreenImage: string;
      regPlateImage: string;
      hideImageOnSmallScreens: boolean;
    };
  };
}
export const InputWidgetContainer: React.FC<WidgetProps> = ({
  enonicData
}: WidgetProps) => {
  const [formData, setFormData] = useState({
    regNr: '',
    ssn: ''
  });

  const MIN_DRIVER_AGE = 18;
  const NEXT_PAGE_URL_PATH = '/links/webshop-car';

  const fieldDefinitions = [
    {
      fieldType: 'regNr',
      fieldName: AppHelper.getText('regInputLabel'),
      validation: {
        rules: ['required', 'regNr'] as ValidationRule[],
      },
      ui: {
        id: 'registration-number',
        labelText: enonicData.regNumberLabel || AppHelper.getText('regInputLabel'),
        name: 'input-registrationNumber',
        className: '',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'reg-nr',
        placeHolder: enonicData.regNumberPlaceHolder || AppHelper.getText('regInputPlaceHolder'),
        prefixImage: <img
          src="https://cdn.gjensidige.no/claims-sweden/sweden-core/images/reg-nr-image.svg"
          alt="Registreringsnummer"
        />
      }
    },
    {
      fieldType: 'ssn',
      fieldName: AppHelper.getText('cprLabelText'),
      validation: {
        rules: ['required', 'ssn', 'ssnAge'] as ValidationRule[],
        validationParams: { minAge: MIN_DRIVER_AGE } as CommonValidationParams,
      },
      ui: {
        id: 'cpr-number',
        labelText: enonicData.cprNumberLabel || AppHelper.getText('cprLabelText'),
        name: 'input-cprNumber',
        className: 'mt-1',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'ssn',
        helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
        isHelpTextAlwaysVisible: true,
        inputMode: 'numeric' as const,
        placeHolder: enonicData.cprNumberPlaceHolder || ''
      }
    }
  ] as const;

  const fieldConfigs: FieldValidation[] = fieldDefinitions.map(field => ({
    fieldType: field.fieldType,
    fieldName: field.fieldName,
    rules: field.validation.rules,
    ...('validationParams' in field.validation && field.validation.validationParams && {
      validationParams: field.validation.validationParams
    })
  }));

  const {
    refs,
    handleBlur,
    handleChange,
    validateAll,
    getFieldError,
    hasFieldError,
  } = useFormValidation(fieldConfigs);

  const createChangeHandler = (fieldType: FieldType) => (event: any) => {
    event.preventDefault();
    let value = event.currentTarget.value;

    if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'regNr') {
      value = value.trim().toUpperCase();
    }

    setFormData(prev => ({ ...prev, [fieldType]: value }));
    handleChange(fieldType, value);
  };

  const createBlurHandler = (fieldType: FieldType) => (event: any) => {
    event.preventDefault();
    let value = event.currentTarget.value;

    // Field-specific processing
    if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'regNr') {
      value = value.trim().toUpperCase();
    }

    handleBlur(fieldType, value);
  };

  const gotoNextStep = (event: any) => {
    event.preventDefault();

    if (!validateAll(formData)) {
      return;
    }

    const customerInfo = {
      cprNumber: formData.ssn,
      registrationNumber: formData.regNr
    };
    sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));
    if (NEXT_PAGE_URL_PATH) {
      window.location.href = window.location.origin + NEXT_PAGE_URL_PATH;
    }
  };
  const carParams: CalculationWidgetProps = {
    headerText: enonicData.headerText || AppHelper.getText('car.widgetTitle'),
    image: enonicData.imageLocations.image,
    smallScreenImage: enonicData.imageLocations.smallScreenImage,
    imageAltText: enonicData.imageAltText || '',
    hideImageOnSmallScreens: enonicData.imageLocations.hideImageOnSmallScreens,
    inputLists: {
      calculationInputs: fieldDefinitions.map(field => ({
        type: 'inputText' as const,
        inputListInRow: [
          {
            ...field.ui,
            onChange: createChangeHandler(field.fieldType),
            onBlur: createBlurHandler(field.fieldType),
            value: formData[field.fieldType as keyof typeof formData],
            invalid: hasFieldError(field.fieldType),
            errorText: getFieldError(field.fieldType),
            ref: refs[field.fieldType]
          }
        ]
      })),
      calculateBtn: {
        btnText:
          enonicData.calculateBtnLabel ||
          AppHelper.getText('calculateBtnLabel'),
        onClick: gotoNextStep,
        enableTracking: true,
        disabled: false,
        className: 'calculate-price mt-2'
      }
    },
    additionalInfoLinkText: AppHelper.getText(
      'personalDataHandlingInfoLinkText'
    ),
    additionalInfoLink: AppHelper.getText('personalDataHandlinkUrl'),
    footerMessage: enonicData.footerMessage || ''
  };

  return <CalculationWidget {...carParams} />;
};
